import React from 'react';
import { Button } from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { config } from '../../utils/utils';

interface Advertiser {
  advertiserId: string;
  name: string;
  userRole: string;
  totalViews: number;
  totalContactClicks: number;
  adsPublished: number;
}

interface AdvertisersPerformanceResponse {
  advertisers: Advertiser[];
  totalAdvertisers: number;
  summary?: {
    totalPublished: number;
    averagePerMonth: number;
    publishedPercentage: number;
    totalViews: number;
  };
  startDate?: string;
  endDate?: string;
}

interface KpiPdfExportProps {
  data: AdvertisersPerformanceResponse | null;
  startDate: Date | null;
  endDate: Date | null;
  chart1Ref: React.RefObject<HTMLDivElement>;
  chart2Ref: React.RefObject<HTMLDivElement>;
  chart3Ref: React.RefObject<HTMLDivElement>;
  tableRef: React.RefObject<HTMLDivElement>;
  loading?: boolean;
  page?: number;
  rowsPerPage?: number;
  formatDateToYYYYMMDD: (date: Date) => string;
  fetchAllData?: () => Promise<AdvertisersPerformanceResponse | null>;
  accessToken?: string;
  selectedRoleType?: string;
  selectedUserId?: string;
  usersList?: Array<{id: string, name: string, userRole?: string, companyId?: string, company?: {name: string}}>;
}

const KpiPdfExport: React.FC<KpiPdfExportProps> = ({
  data,
  startDate,
  endDate,
  chart1Ref,
  chart2Ref,
  chart3Ref,
  tableRef: _tableRef,
  loading = false,
  page: _page = 0,
  rowsPerPage: _rowsPerPage = 10,
  formatDateToYYYYMMDD,
  fetchAllData,
  accessToken,
  selectedRoleType = '',
  selectedUserId = '',
  usersList = [],
}) => {
  const [exporting, setExporting] = React.useState<boolean>(false);

  const handleExportPDF = async () => {
    if (!data) return;

    setExporting(true);
    try {
      // Fetch all data for PDF export (not just current page)
      let allAdvertisersData = data.advertisers;
      if (fetchAllData) {
        try {
          const allData = await fetchAllData();
          if (allData && allData.advertisers && allData.advertisers.length > 0) {
            allAdvertisersData = allData.advertisers;
          }
        } catch (error) {
          console.error('Error fetching all data for PDF:', error);
          // Fall back to current page data if fetch fails
        }
      }
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let yPosition = margin;

      // Helper function to add a new page if needed
      const checkPageBreak = (requiredHeight: number) => {
        if (yPosition + requiredHeight > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };

      // Generate report title based on filters
      let reportSubtitle = '';
      
      // Role labels mapping
      const roleLabels: { [key: string]: string } = {
        'individual': 'Individual',
        'company': 'Company',
        'broker': 'Broker',
      };
      
      if (selectedUserId && usersList.length > 0) {
        // If a specific user is selected, show role: name
        const selectedUser = usersList.find(user => user.id === selectedUserId);
        if (selectedUser) {
          // Check if selected user is an agent - show company and agent name
          if (selectedUser.userRole === 'agent') {
            // Try to get company name from usersList (if company object is included)
            let companyName = selectedUser.company?.name;
            
            // If company name not in usersList, try to find it from allAdvertisersData
            if (!companyName) {
              // Look for the company in the usersList (companies have userRole === 'company')
              if (selectedUser.companyId) {
                const companyUser = usersList.find(u => u.id === selectedUser.companyId && u.userRole === 'company');
                companyName = companyUser?.name;
              }
            }
            
            // If still no company name, try to fetch it
            if (!companyName && selectedUser.companyId && accessToken) {
              try {
                const companyResponse = await fetch(
                  `${config.BASE_URL}/users/${selectedUser.companyId}`,
                  {
                    headers: {
                      'Authorization': `Bearer ${accessToken}`,
                      'Content-Type': 'application/json'
                    }
                  }
                );
                if (companyResponse.ok) {
                  const companyData = await companyResponse.json();
                  companyName = companyData.name;
                }
              } catch (error) {
                console.error('Error fetching company name:', error);
              }
            }
            
            companyName = companyName || 'Unknown Company';
            reportSubtitle = `Company: ${companyName} & Agent: ${selectedUser.name}`;
          } else {
            // For non-agent users, show role: name
            const roleLabel = selectedRoleType ? roleLabels[selectedRoleType] || 'User' : 'User';
            reportSubtitle = `${roleLabel}: ${selectedUser.name}`;
          }
        } else {
          // Fallback: try to get name from data
          const userFromData = allAdvertisersData.find(ad => ad.advertiserId === selectedUserId);
          if (userFromData) {
            // Check if user from data is an agent
            if (userFromData.userRole === 'agent') {
              // Try to find company name from usersList
              const agentUser = usersList.find(u => u.id === selectedUserId);
              let companyName = agentUser?.company?.name;
              
              if (!companyName && agentUser?.companyId) {
                const companyUser = usersList.find(u => u.id === agentUser.companyId && u.userRole === 'company');
                companyName = companyUser?.name;
              }
              
              companyName = companyName || 'Unknown Company';
              reportSubtitle = `Company: ${companyName} & Agent: ${userFromData.name}`;
            } else {
              const roleLabel = selectedRoleType ? roleLabels[selectedRoleType] || 'User' : 'User';
              reportSubtitle = `${roleLabel}: ${userFromData.name}`;
            }
          } else {
            const roleLabel = selectedRoleType ? roleLabels[selectedRoleType] || 'User' : 'User';
            reportSubtitle = `${roleLabel}`;
          }
        }
      } else if (selectedRoleType) {
        // If role is selected but no specific user
        reportSubtitle = `All ${roleLabels[selectedRoleType] || 'Users'}`;
      } else {
        // No filters applied
        reportSubtitle = 'All Users';
      }

      // Add Main Title
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text('KPI Dashboard Report', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 8;

      // Add Report Subtitle (filter-based)
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text(reportSubtitle, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;

      // Add Date Range
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      const dateRangeText = `Date Range: ${startDate ? formatDateToYYYYMMDD(startDate) : 'N/A'} to ${endDate ? formatDateToYYYYMMDD(endDate) : 'N/A'}`;
      pdf.text(dateRangeText, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 15;

      // Export Chart 1: Monthly Registered Users
      if (chart1Ref.current) {
        checkPageBreak(80);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Monthly Registered Users', margin, yPosition);
        yPosition += 8;

        const canvas1 = await html2canvas(chart1Ref.current, {
          backgroundColor: '#ffffff',
          scale: 2,
        });
        const imgData1 = canvas1.toDataURL('image/png');
        const imgWidth1 = pageWidth - 2 * margin;
        const imgHeight1 = (canvas1.height * imgWidth1) / canvas1.width;

        checkPageBreak(imgHeight1);
        pdf.addImage(imgData1, 'PNG', margin, yPosition, imgWidth1, imgHeight1);
        yPosition += imgHeight1 + 10;
      }

      // Export Chart 2: Monthly Published Ads
      if (chart2Ref.current) {
        checkPageBreak(80);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Monthly Published Ads', margin, yPosition);
        yPosition += 8;

        const canvas2 = await html2canvas(chart2Ref.current, {
          backgroundColor: '#ffffff',
          scale: 2,
        });
        const imgData2 = canvas2.toDataURL('image/png');
        const imgWidth2 = pageWidth - 2 * margin;
        const imgHeight2 = (canvas2.height * imgWidth2) / canvas2.width;

        checkPageBreak(imgHeight2);
        pdf.addImage(imgData2, 'PNG', margin, yPosition, imgWidth2, imgHeight2);
        yPosition += imgHeight2 + 5;
      }

      // Export Chart 3: Ads Monthly Status
      if (chart3Ref.current) {
        checkPageBreak(80);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Ads Monthly Status', margin, yPosition);
        yPosition += 8;

        const canvas3 = await html2canvas(chart3Ref.current, {
          backgroundColor: '#ffffff',
          scale: 2,
        });
        const imgData3 = canvas3.toDataURL('image/png');
        const imgWidth3 = pageWidth - 2 * margin;
        const imgHeight3 = (canvas3.height * imgWidth3) / canvas3.width;

        checkPageBreak(imgHeight3);
        pdf.addImage(imgData3, 'PNG', margin, yPosition, imgWidth3, imgHeight3);
        yPosition += imgHeight3 + 5;
      }

      // Export Table - Hybrid Approach (html2canvas + text fallback)
      if (allAdvertisersData.length > 0) {
        checkPageBreak(30);
        
        // Title
        pdf.setFontSize(18);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Advertisers Statistics', margin, yPosition);
        yPosition += 6;
        
        // Total count
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(25, 118, 210);
        pdf.text(`Total Advertisers: ${allAdvertisersData.length}`, margin, yPosition);
        yPosition += 8;

        // Try to capture table with html2canvas first (supports Arabic)
        // Split into chunks to handle page breaks properly
        let tableCaptured = false;
        const rowsPerChunk = 30; // Number of rows per table chunk
        const totalChunks = Math.ceil(allAdvertisersData.length / rowsPerChunk);

        try {
          for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
            const startIndex = chunkIndex * rowsPerChunk;
            const endIndex = Math.min(startIndex + rowsPerChunk, allAdvertisersData.length);
            const chunkData = allAdvertisersData.slice(startIndex, endIndex);

            // Create a temporary container for this chunk
            const tempContainer = document.createElement('div');
            tempContainer.style.position = 'absolute';
            tempContainer.style.left = '-9999px';
            tempContainer.style.top = '0';
            tempContainer.style.width = '800px';
            tempContainer.style.backgroundColor = '#ffffff';
            tempContainer.style.padding = '10px';
            document.body.appendChild(tempContainer);

            // Create table for this chunk
            const tempTable = document.createElement('table');
            tempTable.style.width = '100%';
            tempTable.style.borderCollapse = 'collapse';
            tempTable.style.fontFamily = 'Arial, sans-serif';
            tempTable.style.fontSize = '12px';

            // Create header (only for first chunk, or repeat for each chunk)
            const thead = document.createElement('thead');
            const headerRow = document.createElement('tr');
            headerRow.style.backgroundColor = '#1976d2';
            headerRow.style.color = '#ffffff';
            ['S.No', 'Advertiser', 'Role', 'Views', 'Clicks', 'Ads Published'].forEach(headerText => {
              const th = document.createElement('th');
              th.textContent = headerText;
              th.style.padding = '8px';
              th.style.border = '1px solid #ddd';
              th.style.textAlign = 'left';
              headerRow.appendChild(th);
            });
            thead.appendChild(headerRow);
            tempTable.appendChild(thead);

            // Create body with chunk rows
            const tbody = document.createElement('tbody');
            chunkData.forEach((advertiser, localIndex) => {
              const globalIndex = startIndex + localIndex;
              const row = document.createElement('tr');
              if (globalIndex % 2 === 0) {
                row.style.backgroundColor = '#fafafa';
              }
              const rowData = [
                (globalIndex + 1).toString(),
                advertiser.name || 'Unknown',
                advertiser.userRole,
                advertiser.totalViews.toLocaleString(),
                advertiser.totalContactClicks.toLocaleString(),
                advertiser.adsPublished.toString(),
              ];
              rowData.forEach((cellText, cellIndex) => {
                const td = document.createElement('td');
                td.textContent = cellText;
                td.style.padding = '8px';
                td.style.border = '1px solid #ddd';
                if (cellIndex >= 2) {
                  td.style.textAlign = 'center';
                }
                row.appendChild(td);
              });
              tbody.appendChild(row);
            });
            tempTable.appendChild(tbody);
            tempContainer.appendChild(tempTable);

            // Wait for rendering
            await new Promise(resolve => setTimeout(resolve, 100));

            // Capture this chunk
            const canvas = await html2canvas(tempContainer, {
              backgroundColor: '#ffffff',
              scale: 2,
              useCORS: true,
              logging: false,
            });

            // Clean up
            document.body.removeChild(tempContainer);

            // Add chunk to PDF with page break handling
            const imgData = canvas.toDataURL('image/png');
            const imgWidth = pageWidth - 2 * margin;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            
            checkPageBreak(imgHeight);
            pdf.addImage(imgData, 'PNG', margin, yPosition, imgWidth, imgHeight);
            yPosition += imgHeight + 5;
          }
          tableCaptured = true;
        } catch (error) {
          console.error('Error capturing table with html2canvas:', error);
          tableCaptured = false;
        }

        // Fallback to text-based table if html2canvas failed
        if (!tableCaptured) {
          // Table configuration
          const tableHeaders = ['S.No', 'Advertiser', 'Role', 'Views', 'Clicks', 'Ads Published'];
          const colWidths = [15, 70, 25, 25, 25, 30];
          const tableStartX = margin;
          const tableEndX = pageWidth - margin;
          const rowHeight = 8;
          const headerHeight = 10;
          const cellPadding = 3;
          const headerY = yPosition;

          // Header background
          pdf.setFillColor(25, 118, 210);
          pdf.rect(tableStartX, headerY, tableEndX - tableStartX, headerHeight, 'F');

          // Header text
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(255, 255, 255);
          let xPos = tableStartX;
          tableHeaders.forEach((header, index) => {
            const textX = index < 2 ? xPos + cellPadding : xPos + (colWidths[index] / 2) - (pdf.getTextWidth(header) / 2);
            pdf.text(header, textX, headerY + 7);
            xPos += colWidths[index];
          });
          yPosition += headerHeight + 2;
          pdf.setTextColor(0, 0, 0);

          // Table rows
          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'normal');
          allAdvertisersData.forEach((advertiser, index) => {
            checkPageBreak(rowHeight + 2);
            
            const rowY = yPosition;
            
            // Alternate row background
            if (index % 2 === 0) {
              pdf.setFillColor(250, 250, 250);
              pdf.rect(tableStartX, rowY, tableEndX - tableStartX, rowHeight, 'F');
            }

            // Row data
            const rowData = [
              (index + 1).toString(),
              (advertiser.name || 'Unknown').substring(0, 40),
              advertiser.userRole,
              advertiser.totalViews.toLocaleString(),
              advertiser.totalContactClicks.toLocaleString(),
              advertiser.adsPublished.toString(),
            ];

            xPos = tableStartX;
            rowData.forEach((cell, cellIndex) => {
              let textX = xPos + cellPadding;
              
              // Center align for Role and numeric columns
              if (cellIndex >= 2) {
                const textWidth = pdf.getTextWidth(cell);
                textX = xPos + (colWidths[cellIndex] / 2) - (textWidth / 2);
              }
              
              // Bold for numbers
              if (cellIndex >= 3) {
                pdf.setFont('helvetica', 'bold');
              } else {
                pdf.setFont('helvetica', 'normal');
              }
              
              pdf.text(cell, textX, rowY + 6);
              xPos += colWidths[cellIndex];
            });
            
            // Row border
            pdf.setDrawColor(220, 220, 220);
            pdf.setLineWidth(0.1);
            pdf.line(tableStartX, rowY + rowHeight, tableEndX, rowY + rowHeight);
            
            yPosition += rowHeight + 1;
          });
          
          // Outer border
          pdf.setLineWidth(0.3);
          pdf.setDrawColor(180, 180, 180);
          pdf.rect(tableStartX, headerY, tableEndX - tableStartX, yPosition - headerY, 'S');
          
          yPosition += 8;
        }
      }

      // Add summary statistics if available
      if (data.summary) {
        checkPageBreak(30);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Summary Statistics', margin, yPosition);
        yPosition += 8;

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Total Published: ${data.summary.totalPublished}`, margin, yPosition);
        yPosition += 6;
        pdf.text(`Average Per Month: ${data.summary.averagePerMonth}`, margin, yPosition);
        yPosition += 6;
        pdf.text(`Published Percentage: ${data.summary.publishedPercentage}%`, margin, yPosition);
        yPosition += 6;
        pdf.text(`Total Views: ${data.summary.totalViews.toLocaleString()}`, margin, yPosition);
        yPosition += 10;
      }

      // Generate filename with date range
      const filename = `KPI_Dashboard_${startDate ? formatDateToYYYYMMDD(startDate) : 'N/A'}_to_${endDate ? formatDateToYYYYMMDD(endDate) : 'N/A'}.pdf`;

      // Save PDF
      pdf.save(filename);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Button
      variant="contained"
      color="primary"
      startIcon={<GetAppIcon />}
      onClick={handleExportPDF}
      disabled={exporting || !data || loading}
    >
      {exporting ? 'Exporting...' : 'Export PDF'}
    </Button>
  );
};

export default KpiPdfExport;

